import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from "firebase/auth";
import { auth, functions, db } from '../firebaseConfig';
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import NavigationBar from './NavigationBar';
import { useTheme } from '../contexts/ThemeContext';
import { Disclosure, Dialog } from '@headlessui/react';
import { ChevronDown, Mail, MessageSquare, CreditCard, Loader2 } from 'lucide-react';


function AccountPage() {
    const { darkMode } = useTheme();
    const navigate = useNavigate();
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const [hasCanceled, setHasCanceled] = useState(false);

    const [showUserMenu, setShowUserMenu] = useState(false);
    const [user, setUser] = useState(null);
    const [userSettings, setUserSettings] = useState({ earnings_previews_setting: false, earnings_updates_setting: false, earnings_evidence_setting: false });
    const [tickerRequest, setTickerRequest] = useState('');
    const [bugReport, setBugReport] = useState('');
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    useEffect(() => {
        if (auth.currentUser) {
            const userRef = doc(db, "users", auth.currentUser.uid);
            getDoc(userRef).then(docSnap => {
                if (docSnap.exists()) {
                    setUserSettings(docSnap.data());
                }
            });
        } else {
            navigate('/'); // Redirect if not logged in
        }
    }, [navigate]);

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setUserSettings(prev => ({ ...prev, [name]: checked }));
        const userRef = doc(db, "users", auth.currentUser.uid);
        updateDoc(userRef, { [name]: checked });
    };

    const handleSubmitFeedback = (type, value) => {
        const userRef = doc(db, "users", auth.currentUser.uid);
        updateDoc(userRef, { [type]: arrayUnion(value) });
        if (type === 'ticker_requests') setTickerRequest('');
        else setBugReport('');
    };

    useEffect(() => {
        // This example assumes `auth.currentUser` changes are correctly handled elsewhere.
        const unsubscribe = auth.onAuthStateChanged(currentUser => {
            setUser(currentUser);
            if (!currentUser) {
                navigate('/'); // Redirects to home if not authenticated
            }
        });

        return () => unsubscribe();
      }, [navigate]);

    useEffect(() => {
        const getUserSubscriptionStatus = async () => {
            const userRef = doc(db, "users", auth.currentUser.uid);
            const docSnap = await getDoc(userRef);
            if (docSnap.exists()) {
                setHasPaid(docSnap.data().hasPaid);
                setHasCanceled(docSnap.data().hasCanceled);
            } else {
                console.log("No such document!");
            }
        };
        getUserSubscriptionStatus();
    }, []);

    const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
    const handleCancellation = () => {
        setShowCancelConfirmation(true);
    };

    const handleConfirmCancellation = () => {
        setShowCancelConfirmation(false);
        setIsLoading(true);
        cancelSubscription().then(result => {
            console.log(result.data.message);
            setMessage(result.data.message);
            setIsLoading(false);
            setHasCanceled(true);
        }).catch(error => {
            console.error("Error canceling subscription:", error);
            setMessage(`Error: ${error.message}`);
            setIsLoading(false);
        });
    };

    const handleCancelDialog = () => {
        setShowCancelConfirmation(false);
    };

    const renewSubscription = httpsCallable(functions, 'renewSubscription');
    const handleRenew = () => {
        // Replace with the actual URL to renew the subscription
        const userRef = doc(db, "users", auth.currentUser.uid);
        updateDoc(userRef, { hasCanceled: false });
        setIsLoading(true);
        renewSubscription().then(result => {
            console.log(result.data.message);
            setMessage(result.data.message);
            setIsLoading(false);
            setHasCanceled(false);
        }).catch(error => {
            console.error("Error renewing subscription:", error);
            setMessage(`Error: ${error.message}`);
            setIsLoading(false);
        });
        // window.location.href = 'https://buy.stripe.com/fZe2aM2JogZL5ya288';
    };


    const handleSignOut = async () => {
        await signOut(auth);
        navigate('/');  // Redirect to login after sign out
    };

    return (
        <div className="app-route-canvas min-h-screen pb-24 pt-16">
            <NavigationBar
                user={user}
                setShowUserMenu={setShowUserMenu}
                showUserMenu={showUserMenu}
                handleSignOut={handleSignOut}
            />
            <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10">
                <header className="mb-6">
                    <h1 className="mb-2 text-2xl font-semibold tracking-tight">
                        Account Settings
                    </h1>
                    <p className="app-route-muted text-sm">
                        Manage your preferences and subscription
                    </p>
                </header>

                {/* Email Notifications Card */}
                <section className="app-route-surface app-route-border mb-4 overflow-hidden rounded-lg border">
                    <Disclosure>
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="app-route-border flex w-full items-center justify-between border-b px-5 py-4 text-left transition-colors duration-150 hover:bg-[var(--app-surface-muted)]">
                                    <div className="flex items-center gap-3">
                                        <div className={`flex items-center justify-center rounded-md border p-2 ${
                                            darkMode
                                                ? 'bg-[rgba(59,130,246,0.15)] border border-[rgba(59,130,246,0.2)]'
                                                : 'bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.15)]'
                                        }`}>
                                            <Mail className={`w-[1.125rem] h-[1.125rem] ${darkMode ? 'text-[#60A5FA]' : 'text-[#3B82F6]'}`} />
                                        </div>
                                        <span className="text-base font-semibold tracking-tight sm:text-lg">
                                            Email Notifications
                                        </span>
                                    </div>
                                    <ChevronDown className={`app-route-subtle h-5 w-5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
                                </Disclosure.Button>
                                <Disclosure.Panel className="p-5">
                                    <div className="flex flex-col">
                                        <label className="flex items-start gap-3 mb-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={userSettings.earnings_previews_setting}
                                                onChange={handleCheckboxChange}
                                                name="earnings_previews_setting"
                                                className={`w-4 h-4 rounded checked:bg-blue-500 checked:border-blue-500 cursor-pointer accent-blue-500 mt-1 ${darkMode ? 'bg-slate-800 border border-white/20' : 'bg-white border border-gray-300'}`}
                                            />
                                            <div>
                                                <p className="font-medium">
                                                    Earnings Previews
                                                </p>
                                                <p className="app-route-muted text-sm">
                                                    Get notified before earnings calls
                                                </p>
                                            </div>
                                        </label>
                                        <label className="flex items-start gap-3 mb-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={userSettings.earnings_updates_setting}
                                                onChange={handleCheckboxChange}
                                                name="earnings_updates_setting"
                                                className={`w-4 h-4 rounded checked:bg-blue-500 checked:border-blue-500 cursor-pointer accent-blue-500 mt-1 ${darkMode ? 'bg-slate-800 border border-white/20' : 'bg-white border border-gray-300'}`}
                                            />
                                            <div>
                                                <p className="font-medium">
                                                    Earnings Updates
                                                </p>
                                                <p className="app-route-muted text-sm">
                                                    Receive updates on earnings results
                                                </p>
                                            </div>
                                        </label>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={userSettings.earnings_evidence_setting}
                                                onChange={handleCheckboxChange}
                                                name="earnings_evidence_setting"
                                                className={`w-4 h-4 rounded checked:bg-blue-500 checked:border-blue-500 cursor-pointer accent-blue-500 mt-1 ${darkMode ? 'bg-slate-800 border border-white/20' : 'bg-white border border-gray-300'}`}
                                            />
                                            <div>
                                                <p className="font-medium">
                                                    New Disconfirming Evidence
                                                </p>
                                                <p className="app-route-muted text-sm">
                                                    Alerts when new evidence contradicts predictions
                                                </p>
                                            </div>
                                        </label>
                                    </div>
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                </section>

                {/* Feedback Card */}
                <section className="app-route-surface app-route-border mb-4 overflow-hidden rounded-lg border">
                    <Disclosure>
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="app-route-border flex w-full items-center justify-between border-b px-5 py-4 text-left transition-colors duration-150 hover:bg-[var(--app-surface-muted)]">
                                    <div className="flex items-center gap-3">
                                        <div className={`flex items-center justify-center rounded-md border p-2 ${
                                            darkMode
                                                ? 'bg-[rgba(251,191,36,0.15)] border border-[rgba(251,191,36,0.2)]'
                                                : 'bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.15)]'
                                        }`}>
                                            <MessageSquare className={`w-[1.125rem] h-[1.125rem] ${darkMode ? 'text-[#FBBF24]' : 'text-[#F59E0B]'}`} />
                                        </div>
                                        <span className="text-base font-semibold tracking-tight sm:text-lg">
                                            Feedback & Requests
                                        </span>
                                    </div>
                                    <ChevronDown className={`app-route-subtle h-5 w-5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
                                </Disclosure.Button>
                                <Disclosure.Panel className="p-5">
                                    <div className="mb-5">
                                        <p className="mb-2 font-medium">
                                            Request New Tickers
                                        </p>
                                        <textarea
                                            value={tickerRequest}
                                            onChange={e => setTickerRequest(e.target.value)}
                                            placeholder="Which companies would you like us to add?"
                                            rows={2}
                                            className="app-route-surface-muted app-route-border mb-3 w-full resize-none rounded-md border p-4 text-sm outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 dark:placeholder:text-white/40"
                                        />
                                        <button
                                            onClick={() => handleSubmitFeedback('ticker_requests', tickerRequest)}
                                            className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-700"
                                        >
                                            Submit Request
                                        </button>
                                    </div>
                                    <div>
                                        <p className="mb-2 font-medium">
                                            Bug Reports & Feature Requests
                                        </p>
                                        <textarea
                                            value={bugReport}
                                            onChange={e => setBugReport(e.target.value)}
                                            placeholder="Report bugs or suggest new features..."
                                            rows={3}
                                            className="app-route-surface-muted app-route-border mb-3 w-full resize-none rounded-md border p-4 text-sm outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 dark:placeholder:text-white/40"
                                        />
                                        <button
                                            onClick={() => handleSubmitFeedback('bugs_or_features', bugReport)}
                                            className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-700"
                                        >
                                            Submit Feedback
                                        </button>
                                    </div>
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                </section>

                {/* Billing Card */}
                <section className="app-route-surface app-route-border overflow-hidden rounded-lg border">
                    <Disclosure>
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="app-route-border flex w-full items-center justify-between border-b px-5 py-4 text-left transition-colors duration-150 hover:bg-[var(--app-surface-muted)]">
                                    <div className="flex items-center gap-3">
                                        <div className={`flex items-center justify-center rounded-md border p-2 ${
                                            darkMode
                                                ? 'bg-[rgba(16,185,129,0.15)] border border-[rgba(16,185,129,0.2)]'
                                                : 'bg-[rgba(16,185,129,0.1)] border border-[rgba(16,185,129,0.15)]'
                                        }`}>
                                            <CreditCard className={`w-[1.125rem] h-[1.125rem] ${darkMode ? 'text-[#34D399]' : 'text-[#10B981]'}`} />
                                        </div>
                                        <span className="text-base font-semibold tracking-tight sm:text-lg">
                                            Billing & Subscription
                                        </span>
                                    </div>
                                    <ChevronDown className={`app-route-subtle h-5 w-5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
                                </Disclosure.Button>
                                <Disclosure.Panel className="p-5">
                                    <div className="flex flex-col gap-3">
                                        {message && (
                                            <div className="app-route-surface-muted app-route-border rounded-md border p-4">
                                                <p className="app-route-muted text-sm">
                                                    {message}
                                                </p>
                                            </div>
                                        )}

                                        {hasPaid && !hasCanceled && (
                                            <button
                                                onClick={handleCancellation}
                                                disabled={isLoading}
                                                className="rounded-md bg-red-600 px-5 py-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-600"
                                            >
                                                {isLoading ? (
                                                    <span className="flex items-center gap-2 justify-center">
                                                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                                                        Processing...
                                                    </span>
                                                ) : (
                                                    'Cancel Subscription'
                                                )}
                                            </button>
                                        )}

                                        {hasPaid && hasCanceled && (
                                            <button
                                                onClick={handleRenew}
                                                className="rounded-md bg-emerald-600 px-5 py-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-emerald-700"
                                            >
                                                Renew Subscription
                                            </button>
                                        )}

                                        {!hasPaid && (
                                            <div className="app-route-surface-muted app-route-border rounded-md border p-5 text-center">
                                                <p className="app-route-muted mb-1 text-sm">
                                                    No active subscription
                                                </p>
                                                <p className="app-route-subtle text-xs">
                                                    Upgrade to access premium features
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </Disclosure.Panel>
                            </>
                        )}
                    </Disclosure>
                </section>
            </main>

            {/* Cancellation Confirmation Dialog */}
            <Dialog
                open={showCancelConfirmation}
                onClose={handleCancelDialog}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/55" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="app-route-surface app-route-border mx-auto max-w-md rounded-lg border p-0 shadow-xl">
                        <Dialog.Title className="app-route-border border-b px-6 py-5 text-lg font-semibold">
                            Confirm Cancellation
                        </Dialog.Title>
                        <div className="px-6 pt-5 pb-3">
                            <p className="app-route-muted text-sm leading-relaxed">
                                Are you sure you want to cancel your subscription? This action cannot be undone and you will lose access to all premium features.
                            </p>
                        </div>
                        <div className="app-route-border flex justify-end gap-3 border-t px-6 py-5">
                            <button
                                onClick={handleCancelDialog}
                                className="app-route-surface-muted app-route-border rounded-md border px-5 py-2.5 text-sm font-medium transition-colors duration-150 hover:brightness-95 dark:hover:brightness-110"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmCancellation}
                                className="rounded-md bg-red-600 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
                            >
                                Yes, Cancel Subscription
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </div>
    );
}

export default AccountPage;
